// Строка итогов табеля: должно, вышло, недобор, укомплектованность.
//
// Четыре величины, и у каждой три возможных состояния — значение, ноль по расчёту, неизвестно.
// Правило 7 требует различать их ПО ФОРМЕ, а не оттенком, и здесь это не эстетика: норматив не
// заполнен у 24 объектов заказчика из 60, то есть «неизвестно» — обычное состояние экрана, а не
// краевой случай. Прочерк на месте недобора читался бы как «объект укомплектован ровно».
//
// ## Причина называется ОДИН раз, а не в каждой величине
//
// Правило появилось 25.08.2026 после живого прогона. На пустом табеле экран выглядел так:
// «Вышло — за этот месяц не отмечено ни одной смены, ждём табельщика», «Недобор — пока табель
// пуст, недобор равен всему нормативу и ничего не означает, ждём табельщика»,
// «Укомплектованность — считать нечего: смен за месяц не отмечено, ждём табельщика». Три
// пересказа одного факта подряд и «ждём табельщика» трижды: сорок два слова там, где фактов
// ровно два — смен нет и ждём табельщика.
//
// Человек, читающий три разных формулировки об одном, ищет между ними разницу и не находит.
// Поэтому причина, которая гасит несколько величин сразу, пишется одной фразой под рядом, а в
// самих величинах остаётся короткая пометка, отличающая «неизвестно» от нуля.

import { RiAlertLine } from '@remixicon/react';

import type { TotalsOut } from '@/shared/api/endpoints/timesheet';
import { formatHours } from '@/shared/hours/format';
import { formatDateOnly } from '@/shared/time/format';
import { Metric, MetricRow } from '@/shared/ui/metric';
import { cn } from '@/utils/cn';

/**
 * Общая причина, по которой часть величин посчитать не из чего.
 *
 * Возвращает `null`, когда считать есть из чего: тогда под рядом не появляется ничего.
 *
 * Причина говорит, ГДЕ и КАК вносятся часы, а не кого ждать. До 06.10.2026 здесь стояло «Ждём
 * табельщика», а у заказчика табель ведёт сам финансовый директор: после очистки данных он
 * вносит август руками и читал бы это как «ждать кого-то другого».
 */
export function общаяПричина(totals: TotalsOut): string | null {
  const безНорматива = totals.norm === null;
  const безСмен = !totals.has_any_shift;
  if (безНорматива && безСмен) {
    return (
      'Норматива нет и смен за месяц не отмечено: считать не из чего. Норматив задаётся ' +
      'в карточке объекта, часы вносятся здесь: вставкой из Excel или отметкой смен.'
    );
  }
  if (безНорматива) {
    return (
      'Норматив за месяц не задан: недобор и укомплектованность считать не от чего. ' +
      'Задайте его в карточке объекта.'
    );
  }
  if (безСмен) {
    return (
      'Смен за месяц не отмечено: вышло, недобор и укомплектованность считать не из чего. ' +
      'Часы вносятся здесь: вставкой из Excel или отметкой смен.'
    );
  }
  return null;
}

export function TotalsBar({
  totals,
  проблемныеДни = [],
  наКакойСмотрим = 0,
  показатьДень,
}: {
  totals: TotalsOut;
  /** Дни, где объект не закрыт. Пусто — показывать нечего, величина остаётся неподвижной. */
  проблемныеДни?: readonly string[];
  /** Какой из них откроется по нажатию: дней бывает несколько, и ходим по кругу. */
  наКакойСмотрим?: number;
  показатьДень?: () => void;
}) {
  const недоборЧисло = totals.shortfall === null ? null : Number(totals.shortfall);
  const безСмен = !totals.has_any_shift;
  const безНорматива = totals.norm === null;
  const причина = общаяПричина(totals);

  // Пометка под величиной нужна ровно тогда, когда причину больше негде прочитать. Как только
  // под рядом появилась развёрнутая фраза, короткая пометка становится её пересказом: снимок
  // живого табеля 26.08.2026 показал «смен не отмечено» три раза подряд и следом фразу «Смен
  // за месяц не отмечено…» — один факт четырьмя способами. Прочерк отличает «не знаем» от
  // нуля и без пометки.
  const пометка = (короткая: string | null) => (причина === null ? короткая : null);

  // Ряд тот же, что у итогов ведомости в соседней вкладке: четыре доли с разделителями.
  // Раньше «Должно быть» шло без всякой подложки, а три остальных — в серых коробках разной
  // высоты, и один ряд равнозначных величин выглядел набором из четырёх разных элементов.
  return (
    <div className='border-b border-stroke-soft-200 px-5 py-2 узко:px-4'>
      <MetricRow columns={4}>
        <Величина
          подпись='Должно быть'
          единица='ч'
          значение={totals.norm}
          пометка={пометка(безНорматива ? 'норматива нет' : null)}
        />
        <Величина
          подпись='Вышло'
          единица='ч'
          // Ноль при пустом табеле — НЕ результат расчёта, а отсутствие данных, и показывать
          // его числом нельзя: «вышло 0» читается как «никто не выходил», то есть как готовый
          // ответ. Ноль здесь бывает настоящим только когда смены отмечены, но все они невыход.
          значение={безСмен ? null : totals.fact}
          пометка={пометка(безСмен ? 'смен не отмечено' : null)}
        />
        <Величина
          подпись='Недобор'
          единица='ч'
          // Пока табель не заполняли, недобор равен всему нормативу — число верное, но
          // бессмысленное: оно означает не «людей не хватило», а «ещё не отмечали».
          значение={totals.shortfall === null || безСмен ? null : totals.shortfall}
          пометка={пометка(безНорматива ? 'норматива нет' : безСмен ? 'смен не отмечено' : null)}
          // Слово БЕЗ числа: число уже стоит крупно строкой выше, и «недобор 84 / не хватает
          // 84» человек читает как две разные величины и ищет между ними разницу.
          словами={
            недоборЧисло === null || безСмен
              ? null
              : недоборЧисло === 0
                ? 'закрыт ровно'
                : недоборЧисло > 0
                  ? 'столько часов не закрыто'
                  : 'столько часов переработано'
          }
          тревожно={!безСмен && недоборЧисло !== null && недоборЧисло > 0}
          // «А где именно?» — дословный вопрос заказчика 21.09.2026. Величина перестаёт быть
          // тупиком: под ней сказано, в какие дни не закрыто, и нажатие ведёт прямо к ним.
          // Кнопка ВЫГЛЯДИТ кнопкой: нажимаемое число, ничем не отличающееся от неподвижного,
          // никто не нажмёт — в него надо сначала догадаться ткнуть.
          переход={
            проблемныеДни.length === 0 || показатьДень === undefined
              ? undefined
              : {
                  слова:
                    проблемныеДни.length === 1
                      ? `Показать день: ${датаКоротко(проблемныеДни[0] ?? '')}`
                      : `Показать ${проблемныеДни.length === 2 ? 'оба дня' : `дни (${проблемныеДни.length})`}: ${проблемныеДни.map(датаКоротко).join(', ')}`,
                  подпись:
                    проблемныеДни.length === 1
                      ? 'Показать день'
                      : `Показать день ${наКакойСмотрим + 1} из ${проблемныеДни.length}`,
                  нажать: показатьДень,
                }
          }
        />
        <Величина
          подпись='Укомплектованность'
          значение={totals.percent === null || безСмен ? null : `${totals.percent}`}
          единица='%'
          пометка={пометка(безНорматива ? 'норматива нет' : безСмен ? 'смен не отмечено' : null)}
        />
      </MetricRow>
      {причина !== null && (
        <p className='mt-2 flex items-start gap-1.5 text-paragraph-xs text-text-sub-600'>
          <RiAlertLine className='mt-0.5 size-3.5 shrink-0 text-text-soft-400' />
          {причина}
        </p>
      )}
    </div>
  );
}

function Величина({
  подпись,
  значение,
  единица,
  пометка,
  словами,
  тревожно,
  переход,
}: {
  подпись: string;
  значение: string | null;
  единица?: string;
  /** Короткое «почему неизвестно». Развёрнутая причина стоит одна на весь ряд, под ним. */
  пометка: string | null;
  словами?: string | null;
  тревожно?: boolean;
  /** Куда ведёт величина, если ей есть куда вести: слова под числом и само действие. */
  переход?: { слова: string; подпись: string; нажать: () => void };
}) {
  // Неизвестно: прочерк ПЛЮС пометка. Один прочерк читался бы как «недобора нет», то есть как
  // готовый ответ; пометка отличает «не знаем» от нуля, а причину целиком человек читает под
  // рядом — одну на все погашенные величины, а не по разу в каждой.
  if (значение === null) {
    return (
      <Metric
        label={подпись}
        value={<span className='text-text-sub-600'>—</span>}
        note={пометка ?? undefined}
      />
    );
  }
  return (
    <div className='flex min-w-0 flex-col gap-0.5'>
      <span className='truncate text-paragraph-xs text-text-sub-600'>{подпись}</span>
      <span
        className={cn(
          'text-label-lg tabular-nums text-text-strong-950',
          тревожно === true && 'text-error-base',
        )}
      >
        {/* Единица пишется всегда, потому что в одном ряду стоят часы и проценты: «744» без
            подписи — это «744 чего». Число проходит через формат часов и для процента тоже: с
            сервера он приходит строкой с точкой («93.55»), а во всём продукте дробная часть
            отделяется запятой. */}
        {единица === undefined ? formatHours(значение) : `${formatHours(значение)} ${единица}`}
      </span>
      {словами != null && (
        // Цвет ДУБЛИРУЕТ слово, а не заменяет: «−2» человек читает как ошибку, «переработка»
        // — однозначно.
        <span
          className={cn(
            'text-paragraph-xs text-text-sub-600',
            тревожно === true && 'text-error-base',
          )}
        >
          {словами}
        </span>
      )}
      {переход !== undefined && (
        <button
          type='button'
          title={переход.слова}
          onClick={переход.нажать}
          className='w-fit rounded-md text-paragraph-xs text-primary-base underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-base'
        >
          {переход.подпись}
        </button>
      )}
    </div>
  );
}

/** «2026-09-27» → «27 сентября». Дата в предложении пишется по-русски, а не машинно. */
function датаКоротко(день: string): string {
  return formatDateOnly(день);
}
